import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Button,
  ConfigProvider,
  Empty,
  Input,
  Select,
  Skeleton,
  Space,
  Spin,
  Tabs,
  message,
} from "antd";
import {
  FileExcelOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import SummaryCards from "./SummaryCards";
import ConsolidationTree from "./ConsolidationTree";
import PlannedVsActualTree from "./PlannedVsActualTree";
import TimePhasingTree from "./TimePhasingTree";
import TaskDetailDrawer from "./TaskDetailDrawer";
import {
  ThreeMConsolidationData,
  ThreeMPhaseNode,
  ThreeMTimePhasingData,
  buildPhasingIndex,
} from "./threeMShared";
import { useFontScale } from "../../../../theme/fontScale";
import WbsCodeToggle from "../../shared/WbsCodeToggle";
import { useWbsName } from "../../shared/useWbsCodes";

// Project-level 3M (Material / Manpower / Machinery) consolidation report.
// Project context comes the same way as the sibling project-scoped pages
// (WBSViewNew and co.): location.state { id, projectName }, with the optional
// :projectId route param as fallback.

const ThreeMConsolidationReport = () => {
  const dispatch = useDispatch();
  // orgName / orgLogo are present at runtime but undeclared on the slice's
  // Data interface — same access the cost-analysis exports use.
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgName: string | undefined = currentUser?.orgName;
  const orgLogoUrl: string | undefined = currentUser?.orgLogo;
  const location = useLocation();
  const params = useParams();
  const { projectName: stateProjectName, id: stateProjectId } =
    (location.state as any) || {};
  const projectId = stateProjectId ?? params.projectId;

  const [data, setData] = useState<ThreeMConsolidationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters sent to the API. Search is debounced from searchInput.
  const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
  const [activityId, setActivityId] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Phase/Activity dropdown options come from the last unfiltered load, so
  // narrowing the data never empties the dropdowns themselves.
  const [basePhases, setBasePhases] = useState<ThreeMPhaseNode[]>([]);

  const [drawer, setDrawer] = useState<{
    open: boolean;
    taskId?: number;
    taskName?: string;
  }>({ open: false });

  const [refreshTick, setRefreshTick] = useState(0);
  const [exporting, setExporting] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // "3M Breakdown" (planned rollup + norms) | "Planned vs Actual" comparison.
  const [activeTab, setActiveTab] = useState<
    "breakdown" | "pva" | "phasing"
  >("breakdown");
  const [phasing, setPhasing] = useState<ThreeMTimePhasingData | null>(null);

  // App-wide A/A+/A++ preference (switcher lives in the main header). Note:
  // while this report is browser-fullscreened the header is hidden, so the
  // size can only be changed after exiting fullscreen.
  const { fs } = useFontScale();

  // Fullscreen: the report container goes fullscreen via the browser API.
  // Popups (Select dropdowns, tooltips) and the task drawer are re-targeted
  // INTO the container — portals left on document.body would be invisible
  // while another element is fullscreen.
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    // Covers both the button and ESC (the browser exits without telling us).
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen is not supported in this browser");
      });
    }
  };

  const popupContainer = () => containerRef.current || document.body;

  // Project top-menu, same as WBSViewNew
  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Debounce free-text search → API param
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(searchInput.trim()), 450);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchInput]);

  useEffect(() => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const isUnfiltered = !phaseId && !activityId && !search;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Both payloads in ONE Promise.all with ONE setState: fetched
        // separately there would be a window where the tree is filtered to a
        // phase while the phasing cells are still project-wide, which makes
        // Sched % silently wrong instead of visibly broken.
        const [response, phasingResponse] = await Promise.all([
          threeMReportApis.get3MConsolidation(projectId, {
            phaseId,
            activityId,
            search,
          }),
          threeMReportApis.get3MTimePhasing(projectId, {
            phaseId,
            activityId,
            search,
          }),
        ]);
        const { status, data: payload } = response || {};
        if (cancelled) return;
        if (status) {
          setData(payload || {});
          // Additive: a project with no schedules still gets the other tabs.
          setPhasing(phasingResponse?.status ? phasingResponse.data || null : null);
          if (isUnfiltered) setBasePhases(payload?.phases || []);
        } else {
          setError(response?.message || "Failed to load 3M consolidation");
          message.error(response?.message || "Failed to load 3M consolidation");
        }
      } catch (err) {
        console.error("Error loading 3M consolidation:", err);
        if (!cancelled) {
          setError("Error loading 3M Consolidation Report");
          message.error("Error loading 3M Consolidation Report");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, phaseId, activityId, search, refreshTick]);

  // Memoised on [cells, phases] ONLY — the measure and FY selectors inside the
  // Time Phasing tab must not rebuild this, or switching them would re-walk the
  // whole tree.
  const phasingIndex = useMemo(
    () => buildPhasingIndex(phasing?.cells, data?.phases),
    [phasing?.cells, data?.phases]
  );

  const projectName = data?.projectName || stateProjectName || "Project";
  const wbsName = useWbsName();

  const phaseOptions = useMemo(
    () =>
      basePhases.map((phase) => ({
        label: wbsName(phase.phaseName),
        value: phase.phaseId,
      })),
    [basePhases, wbsName]
  );

  const activityOptions = useMemo(() => {
    if (!phaseId) return [];
    const phase = basePhases.find((p) => p.phaseId === phaseId);
    return (phase?.activities || []).map((activity) => ({
      label: wbsName(activity.activityName),
      value: activity.activityId,
    }));
  }, [basePhases, phaseId, wbsName]);

  const hasFilters = !!phaseId || !!activityId || !!searchInput;

  const clearFilters = () => {
    setPhaseId(undefined);
    setActivityId(undefined);
    setSearchInput("");
    setSearch("");
  };

  // Styled multi-sheet Excel export. exceljs and the workbook builder are
  // lazily imported so they stay out of the main bundle; the logo fetch is
  // best-effort (cross-origin blob) and must never fail the export.
  const handleExport = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const phaseName = basePhases.find((p) => p.phaseId === phaseId)?.phaseName;
      const activityName = activityOptions.find(
        (o) => o.value === activityId
      )?.label;
      const helpers = await import("../shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportThreeMWorkbook } = await import("./exportWorkbook");
      await exportThreeMWorkbook({
        data: { ...data, projectName },
        filters: { phaseName, activityName, search },
        phasing: { data: phasing, index: phasingIndex },
        orgName,
        logo,
      });
    } catch (err) {
      console.error("Error exporting 3M Consolidation:", err);
      message.error("Failed to export the workbook");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Head title="3M Consolidation Report" />
      <Content>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            ...(isFullscreen
              ? { background: "#f5f6fa", overflowY: "auto", padding: 16 }
              : {}),
          }}
        >
        <ConfigProvider getPopupContainer={popupContainer}>
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  3M Consolidation Report
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: fs(13) }}>
                  {projectName} · Material / Manpower / Machinery rollup across
                  Phases, Activities and Tasks
                </span>
              </div>
              <Space>
                <Button
                  icon={
                    isFullscreen ? (
                      <FullscreenExitOutlined />
                    ) : (
                      <FullscreenOutlined />
                    )
                  }
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </Button>
                <WbsCodeToggle />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<FileExcelOutlined />}
                  loading={exporting}
                  onClick={handleExport}
                  disabled={!data || loading}
                >
                  Export Excel
                </Button>
              </Space>
            </div>

            {/* Filter bar */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Phase"
                style={{ width: 220 }}
                options={phaseOptions}
                value={phaseId}
                onChange={(value) => {
                  setPhaseId(value);
                  setActivityId(undefined);
                }}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Activity"
                style={{ width: 220 }}
                options={activityOptions}
                value={activityId}
                disabled={!phaseId}
                onChange={(value) => setActivityId(value)}
              />
              <Input
                allowClear
                prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                placeholder="Search task / material / labour / tool"
                style={{ width: 300, maxWidth: "100%" }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {hasFilters ? (
                <Button type="link" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : null}
            </div>

            {loading && !data ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : error && !data ? (
              <Empty description={error} style={{ padding: "60px 0" }} />
            ) : (
              <Spin spinning={loading}>
                <SummaryCards summary={data?.summary} />
                {(data?.phases || []).length === 0 ? (
                  <Empty
                    description={
                      hasFilters
                        ? "No phases / activities / tasks match the current filters"
                        : "No 3M allocation data for this project yet"
                    }
                    style={{ padding: "40px 0" }}
                  />
                ) : (
                  <Tabs
                    activeKey={activeTab}
                    onChange={(key) => setActiveTab(key as "breakdown" | "pva" | "phasing")}
                    items={[
                      {
                        key: "breakdown",
                        label: (
                          <span style={{ fontSize: fs(13.5) }}>
                            3M Breakdown
                          </span>
                        ),
                        children: (
                          <ConsolidationTree
                            phases={data?.phases || []}
                            index={phasingIndex}
                            onViewTask={(taskId, taskName) =>
                              setDrawer({ open: true, taskId, taskName })
                            }
                          />
                        ),
                      },
                      {
                        key: "pva",
                        label: (
                          <span style={{ fontSize: fs(13.5) }}>
                            Planned vs Actual
                          </span>
                        ),
                        children: (
                          <PlannedVsActualTree
                            phases={data?.phases || []}
                            onViewTask={(taskId, taskName) =>
                              setDrawer({ open: true, taskId, taskName })
                            }
                          />
                        ),
                      },
                      {
                        key: "phasing",
                        label: (
                          <span style={{ fontSize: fs(13.5) }}>
                            Time Phasing
                          </span>
                        ),
                        children: (
                          <TimePhasingTree
                            phases={data?.phases || []}
                            phasing={phasing}
                            index={phasingIndex}
                            projectId={projectId}
                            phaseId={phaseId}
                            activityId={activityId}
                            projectName={projectName}
                            orgName={orgName}
                            orgLogoUrl={orgLogoUrl}
                            filtersText={
                              [
                                phaseOptions.find((o) => o.value === phaseId)
                                  ?.label && `Phase: ${phaseOptions.find((o) => o.value === phaseId)?.label}`,
                                activityOptions.find((o) => o.value === activityId)
                                  ?.label && `Activity: ${activityOptions.find((o) => o.value === activityId)?.label}`,
                                search && `Search: "${search}"`,
                              ]
                                .filter(Boolean)
                                .join(" · ") || undefined
                            }
                            getContainer={popupContainer}
                            onViewTask={(taskId, taskName) =>
                              setDrawer({ open: true, taskId, taskName })
                            }
                          />
                        ),
                      },
                    ]}
                  />
                )}
              </Spin>
            )}
          </div>
        </Card>

        <TaskDetailDrawer
          open={drawer.open}
          taskId={drawer.taskId}
          taskName={drawer.taskName}
          onClose={() => setDrawer((d) => ({ ...d, open: false }))}
          getContainer={popupContainer}
        />
        </ConfigProvider>
        </div>
      </Content>
    </>
  );
};

export default ThreeMConsolidationReport;
